import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, FormControl, InputLabel, LinearProgress, MenuItem, Select, TextField, Typography } from '@mui/material';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import PetsOutlinedIcon from '@mui/icons-material/PetsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import { toast } from 'react-toastify';
import {
  createProduction,
  deleteProduction,
  listProductions,
  runProduction,
  updateProduction
} from '../../services/contentAiService';

const LANGUAGE_OPTIONS = [
  { value: 'pt-BR', label: 'Português' },
  { value: 'en-US', label: 'English' },
  { value: 'es-ES', label: 'Español' }
];

const TIME_OPTIONS = [
  { value: 4, label: '4 min' },
  { value: 6, label: '6 min' },
  { value: 8, label: '8 min' },
  { value: 10, label: '10 min' },
  { value: 12, label: '12 min' },
  { value: 14, label: '14 min' }
];

const EDITORIAL_PROFILES = [
  { key: 'epic_documentary', name: 'Epic Documentary', shortDescription: 'Documentários cinematográficos com impacto visual, imersão e ritmo equilibrado.', recommendedMinutes: '12-18 min', idealContentTypes: ['Natureza', 'Espaço', 'Animais', 'Civilizações', 'Grandes eventos'] },
  { key: 'scientific_deep_dive', name: 'Scientific Deep Dive', shortDescription: 'Conteúdo científico aprofundado, com mais precisão, dados, comparações e gráficos.', recommendedMinutes: '12-20 min', idealContentTypes: ['Ciência', 'Tecnologia', 'Saúde', 'Astronomia', 'Biologia'] },
  { key: 'historical_chronicles', name: 'Historical Chronicles', shortDescription: 'Narrativa histórica cronológica com mapas, documentos e linha do tempo.', recommendedMinutes: '12-18 min', idealContentTypes: ['Guerras', 'Impérios', 'Presidentes', 'Copas do Mundo', 'História mundial'] },
  { key: 'adventure_explorer', name: 'Adventure Explorer', shortDescription: 'Ritmo acelerado, descoberta constante e sensação de expedição.', recommendedMinutes: '10-16 min', idealContentTypes: ['Viagens', 'Exploração', 'Montanhas', 'Oceanos', 'Selvas'] },
  { key: 'mystery_files', name: 'Mystery Files', shortDescription: 'Mistério, suspense e investigação com clima sombrio e perguntas recorrentes.', recommendedMinutes: '10-16 min', idealContentTypes: ['Mistérios', 'Arqueologia', 'Fenômenos', 'Casos históricos', 'Descobertas'] },
  { key: 'wild_nature', name: 'Wild Nature', shortDescription: 'Animais e ecossistemas com foco em comportamento animal e contemplação emocional.', recommendedMinutes: '10-18 min', idealContentTypes: ['Animais', 'Oceanos', 'Florestas', 'Ecologia', 'Conservação'] },
  { key: 'modern_explainer', name: 'Modern Explainer', shortDescription: 'Explicações modernas com alta retenção, ritmo equilibrado e visual limpo.', recommendedMinutes: '8-14 min', idealContentTypes: ['Tecnologia', 'Economia', 'Ciência', 'Curiosidades'] },
  { key: 'mini_documentary', name: 'Mini Documentary', shortDescription: 'Documentários compactos otimizados para 8 a 12 minutos e alta retenção.', recommendedMinutes: '8-12 min', idealContentTypes: ['Curiosidades', 'Resumos históricos', 'Ciência compacta', 'Casos rápidos'] }
];

function getEditorialProfileIcon(profileKey) {
  switch (String(profileKey || '').trim()) {
    case 'scientific_deep_dive':
      return ScienceOutlinedIcon;
    case 'historical_chronicles':
      return HistoryEduOutlinedIcon;
    case 'adventure_explorer':
      return ExploreOutlinedIcon;
    case 'mystery_files':
      return PsychologyAltOutlinedIcon;
    case 'wild_nature':
      return PetsOutlinedIcon;
    case 'modern_explainer':
      return InsightsOutlinedIcon;
    case 'mini_documentary':
      return SmartDisplayOutlinedIcon;
    case 'epic_documentary':
    default:
      return MovieFilterOutlinedIcon;
  }
}

function getEditorialProfileLabel(profileKey) {
  return EDITORIAL_PROFILES.find((item) => item.key === String(profileKey || '').trim())?.name || 'Epic Documentary';
}

function fmtDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function getProjectHeadline(row) {
  return String(row?.topic || row?.title || `Projeto #${row?.id || ''}`).trim();
}

function getProjectLanguage(row) {
  return String(row?.metaJson?.language || row?.metaJson?.scriptJson?.language || 'pt-BR').trim() || 'pt-BR';
}

function getProjectMinutes(row) {
  const minutes = Number(row?.metaJson?.minutes || 0) || 8;
  return `${minutes} min`;
}

function getProjectStageLabel(row) {
  const key = String(row?.currentStageKey || '').trim().toLowerCase();
  const map = {
    idea: 'Preparação',
    research: 'Pesquisa',
    knowledge_package: 'Pacote de conhecimento',
    generate_script: 'Roteiro',
    storyboard_engine: 'Storyboard',
    generate_images: 'Assets',
    select_scenes: 'Assets',
    generate_narration: 'Narração',
    process_audio: 'Narração',
    build_timeline: 'Vídeo',
    generate_scene_videos: 'Vídeo',
    render_video: 'Vídeo',
    preview_render: 'Vídeo',
    generate_thumbnail: 'Thumbnail',
    audit: 'Auditoria',
    editorial_review: 'Revisão editorial',
    upload_youtube: 'Publicação',
    publish: 'Publicação',
    analytics_initial: 'Publicação'
  };
  return map[key] || 'Aguardando';
}

export default function ProductionListPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    title: '',
    topic: '',
    language: 'pt-BR',
    editorialProfile: 'epic_documentary',
    minutes: 8
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listProductions({ limit: 80 });
      setRows(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar produções');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRun = useCallback(async (id) => {
    setLoading(true);
    try {
      await runProduction(id);
      toast.success('Produção enfileirada');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar');
    } finally {
      setLoading(false);
    }
  }, [load]);

  const onCreate = useCallback(async () => {
    const title = String(form.title || '').trim();
    const topic = String(form.topic || '').trim();
    const resolvedTitle = title || topic;
    if (!resolvedTitle) {
      toast.error('Informe o título ou o tema do projeto');
      return;
    }
    if (!topic) {
      toast.error('Informe o tema do projeto');
      return;
    }

    const selectedProfile = EDITORIAL_PROFILES.find((item) => item.key === form.editorialProfile) || EDITORIAL_PROFILES[0];
    setLoading(true);
    try {
      const resp = await createProduction({
        title: resolvedTitle,
        topic,
        minutes: Number(form.minutes || 8),
        scriptTemplateKey: 'yt_script_documentario'
      });
      const id = resp?.data?.id || resp?.data?.data?.id || null;
      if (!id) throw new Error('production_id_missing');

      await updateProduction(id, {
        minutes: Number(form.minutes || 8),
        metaJson: {
          language: String(form.language || 'pt-BR'),
          editorialProfile: selectedProfile.key,
          editorialProfileLabel: selectedProfile.name,
          editorialProfileSummary: selectedProfile.shortDescription,
          projectMode: 'minimal_frontend'
        }
      });
      await runProduction(id);
      toast.success('Projeto criado e iniciado');
      setForm({
        title: '',
        topic: '',
        language: 'pt-BR',
        editorialProfile: 'epic_documentary',
        minutes: 8
      });
      await load();
      navigate(`/production/${id}`);
    } catch (e) {
      toast.error(e?.message || 'Falha ao criar projeto');
    } finally {
      setLoading(false);
    }
  }, [form, load, navigate]);

  const onDelete = useCallback(async (id) => {
    const pid = Number(id || 0) || 0;
    if (!pid) return;
    const confirmed = window.confirm(`Excluir a produção #${pid}? Esta ação remove o pipeline, o roteiro, o storyboard e os vínculos do vídeo/upload dessa produção.`);
    if (!confirmed) return;
    setLoading(true);
    try {
      await deleteProduction(pid);
      toast.success('Produção excluída');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao excluir produção');
    } finally {
      setLoading(false);
    }
  }, [load]);

  const selectedEditorialProfile = useMemo(() => (
    EDITORIAL_PROFILES.find((item) => item.key === String(form.editorialProfile || '').trim()) || EDITORIAL_PROFILES[0]
  ), [form.editorialProfile]);
  const SelectedEditorialIcon = getEditorialProfileIcon(selectedEditorialProfile?.key);

  return (
    <Box p={{ xs: 2, md: 3 }} display="grid" gap={3}>
      <Box
        display="grid"
        gap={3}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 4,
          p: { xs: 2, md: 4 },
          background: 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)'
        }}
      >
        <Box display="grid" gap={1}>
          <Typography variant="h4">Novo Projeto</Typography>
          <Typography variant="body1" color="text.secondary">
            Informe apenas título, tema, perfil editorial, duração e idioma. O restante acontece automaticamente.
          </Typography>
        </Box>

        <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}>
          <TextField
            label="Título"
            fullWidth
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Ex.: O Titanic e a engenharia do desastre"
          />
          <TextField
            label="Tema"
            fullWidth
            value={form.topic}
            onChange={(e) => setForm((prev) => ({ ...prev, topic: e.target.value }))}
            placeholder="Ex.: O colapso do Titanic, tubarões, Pelé, buracos negros..."
          />
          <FormControl fullWidth>
            <InputLabel>Idioma</InputLabel>
            <Select
              label="Idioma"
              value={form.language}
              onChange={(e) => setForm((prev) => ({ ...prev, language: e.target.value }))}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Perfil Editorial</InputLabel>
            <Select
              label="Perfil Editorial"
              value={form.editorialProfile}
              onChange={(e) => setForm((prev) => ({ ...prev, editorialProfile: e.target.value }))}
            >
              {EDITORIAL_PROFILES.map((profile) => (
                <MenuItem key={profile.key} value={profile.key}>{profile.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Tempo</InputLabel>
            <Select
              label="Tempo"
              value={String(form.minutes)}
              onChange={(e) => setForm((prev) => ({ ...prev, minutes: Number(e.target.value) }))}
            >
              {TIME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={String(option.value)}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Box display="grid" gap={1}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={`Perfil: ${selectedEditorialProfile.name}`} />
              <Chip label={`Idioma: ${LANGUAGE_OPTIONS.find((item) => item.value === form.language)?.label || form.language}`} />
              <Chip label={`Tempo: ${getProjectMinutes({ metaJson: { minutes: form.minutes } })}`} />
            </Box>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                p: 1.5,
                backgroundColor: 'background.paper'
              }}
              display="grid"
              gap={0.5}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <SelectedEditorialIcon fontSize="small" />
                <Typography variant="subtitle2">{selectedEditorialProfile.name}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {selectedEditorialProfile.shortDescription}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tempo médio recomendado: {selectedEditorialProfile.recommendedMinutes} · Ideal para: {selectedEditorialProfile.idealContentTypes.join(', ')}
              </Typography>
            </Box>
          </Box>
          <Button variant="contained" size="large" onClick={onCreate} disabled={loading || !String(form.topic || '').trim()}>
            Produzir
          </Button>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Box display="grid" gap={0.5}>
          <Typography variant="h5">Projetos</Typography>
          <Typography variant="body2" color="text.secondary">
            Acompanhe apenas o que importa: progresso, etapa atual e o próximo passo.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>Atualizar</Button>
      </Box>

      <Box display="grid" gap={2}>
        {rows.map((row) => {
          const progress = Math.max(0, Math.min(100, Math.round(Number(row?.progressPct || 0) * 100)));
          return (
            <Box
              key={row.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                p: { xs: 2, md: 2.5 },
                backgroundColor: 'background.paper'
              }}
              display="grid"
              gap={1.5}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box display="grid" gap={0.5}>
                  <Typography variant="h6">{getProjectHeadline(row)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Projeto #{row.id} · {getEditorialProfileLabel(row?.metaJson?.editorialProfile)} · {getProjectLanguage(row)} · {getProjectMinutes(row)} · atualizado em {fmtDate(row.updatedAt)}
                  </Typography>
                </Box>
                <Chip label={String(row?.status || 'pending')} color={String(row?.status || '').toLowerCase() === 'failed' ? 'error' : 'default'} />
              </Box>

              <Box display="grid" gap={0.75}>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 999 }} />
                <Typography variant="body2" color="text.secondary">
                  {progress}% · etapa atual: {getProjectStageLabel(row)}
                </Typography>
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="contained" onClick={() => navigate(`/production/${row.id}`)}>Abrir</Button>
                <Button variant="outlined" onClick={() => onRun(row.id)} disabled={loading}>Gerar novamente</Button>
                <Button color="error" variant="outlined" onClick={() => onDelete(row.id)} disabled={loading}>Excluir</Button>
              </Box>
            </Box>
          );
        })}
        {!rows.length ? (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            Nenhum projeto criado ainda.
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
